class CollectionHeaderComponent extends HTMLElement {
  connectedCallback() {
    this.showMoreButton = this.querySelector(".collection-header-info__show-more-button");
    this.paragraphElement = this.querySelector(".collection-header-info__paragraph");
    this.containerElement = this.querySelector(".collection-header-info");

    // Check if content needs truncation after DOM is ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.initializeTruncation());
    } else {
      // DOM is already loaded
      this.initializeTruncation();
    }

    if (this.showMoreButton) {
      this.showMoreButton.addEventListener("click", () => this.expandParagraph());
    }

    // Initialize lastWidth to track viewport size
    this.lastWidth = window.innerWidth;

    // Add a resize event listener with debouncing
    this.resizeTimeout = null;
    this.handleResizeBound = this.handleResize.bind(this);
    window.addEventListener("resize", this.handleResizeBound);
  }

  disconnectedCallback() {
    if (this.showMoreButton) {
      this.showMoreButton.removeEventListener("click", this.expandParagraph);
    }
    window.removeEventListener("resize", this.handleResizeBound);
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }

  initializeTruncation() {
    // Use setTimeout to ensure all web components have rendered
    setTimeout(() => {
      this.checkTruncation();
    }, 150);
  }

  checkTruncation() {
    if (!this.paragraphElement || !this.containerElement) return;

    // Check if paragraph has content
    const content = this.paragraphElement.textContent;
    if (!content || content.trim() === "") {
      return;
    }

    // Only check on mobile/tablet (< 1024px)
    if (window.innerWidth >= 1024) {
      this.containerElement.classList.remove("collection-header-info--truncatable");
      this.containerElement.classList.remove("collection-header-info--expanded");
      return;
    }

    // Check if content is truncated by comparing scrollHeight with clientHeight
    const isOverflowing = this.paragraphElement.scrollHeight > this.paragraphElement.clientHeight;

    // Alternative check: measure actual content height
    if (!isOverflowing) {
      // Double-check by temporarily removing constraints
      const originalMaxHeight = this.paragraphElement.style.maxHeight;
      const originalOverflow = this.paragraphElement.style.overflow;

      this.paragraphElement.style.maxHeight = "none";
      this.paragraphElement.style.overflow = "visible";

      const fullHeight = this.paragraphElement.scrollHeight;

      // Restore original styles
      this.paragraphElement.style.maxHeight = originalMaxHeight;
      this.paragraphElement.style.overflow = originalOverflow;

      // Check if content would overflow when constrained
      const constrainedHeight =
        parseFloat(getComputedStyle(this.paragraphElement).maxHeight) || this.paragraphElement.clientHeight;

      if (fullHeight > constrainedHeight + 5) {
        // 5px tolerance
        this.containerElement.classList.add("collection-header-info--truncatable");
      } else {
        this.containerElement.classList.remove("collection-header-info--truncatable");
        this.containerElement.classList.remove("collection-header-info--expanded");
      }
    } else {
      // Content is overflowing, show the button
      this.containerElement.classList.add("collection-header-info--truncatable");
    }
  }

  expandParagraph() {
    if (this.containerElement) {
      this.containerElement.classList.add("collection-header-info--expanded");
      this.showMoreButton.setAttribute("aria-expanded", "true");
    }
  }

  handleResize() {
    const currentWidth = window.innerWidth;

    // Only trigger resize logic when the width changes
    if (currentWidth !== this.lastWidth) {
      // Debounce the resize handling
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }

      this.resizeTimeout = setTimeout(() => {
        this.checkTruncation();

        // Reset expanded state when resizing to mobile view
        if (currentWidth < 1024 && this.lastWidth >= 1024) {
          this.containerElement.classList.remove("collection-header-info--expanded");
          if (this.showMoreButton) {
            this.showMoreButton.setAttribute("aria-expanded", "false");
          }
        }
      }, 150);
    }

    // Update the last known width
    this.lastWidth = currentWidth;
  }
}

if (!customElements.get("collection-header-info")) {
  customElements.define("collection-header-info", CollectionHeaderComponent);
}
